import { FormEvent, useState } from "react";
import { AlertTriangle, ClipboardList, CreditCard, FileText, LogOut, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { SiteLayout } from "@/components/SiteChrome";
import { trpc } from "@/lib/trpc";

/**
 * The FitLife-X Pro member workspace.
 *
 * Three states, not two. "Signed in" and "entitled" are separate questions, and
 * a lapsed member lands on a page that still carries their billing portal
 * button - locking someone out of the screen where they would fix their card is
 * the single most self-defeating thing this feature could do.
 */

const FORM_LIBRARY = [
  "Membership agreement",
  "Health screening declaration",
  "Liability waiver",
  "Day-guest waiver",
  "Minor / guardian consent",
  "Incident report",
  "Equipment inspection log",
  "Daily opening checklist",
  "Daily closing checklist",
  "Cleaning sign-off sheet",
  "Personal training agreement",
  "Membership cancellation request",
];

const PLACEMENTS = [
  "Newsletter sponsorship",
  "Website vendor bar",
  "Dedicated email",
  "Insight article sponsorship",
  "Vendor directory listing",
];

type Tab = "forms" | "sop" | "advertising" | "requests";

export default function FitLifeXPortal() {
  const me = trpc.fitlifeX.me.useQuery();
  const [tab, setTab] = useState<Tab>("forms");

  const billingPortal = trpc.fitlifeX.billingPortal.useMutation({
    onSuccess: data => window.location.assign(data.url),
    onError: error => toast.error("Billing portal unavailable", { description: error.message }),
  });
  const logout = trpc.fitlifeX.logout.useMutation({
    onSuccess: () => window.location.assign("/fitlife-x/pro"),
  });

  if (me.isLoading) {
    return <SiteLayout><div className="fx-page fx-page--narrow"><section className="section"><div className="site-container">Loading your workspace…</div></section></div></SiteLayout>;
  }

  const session = me.data;
  if (!session || !session.signedIn) {
    return (
      <SiteLayout>
        <div className="fx-page fx-page--narrow">
          <section className="section">
            <div className="site-container fx-auth">
              <p className="eyebrow">FitLife-X Pro</p>
              <h1>Please sign in.</h1>
              <p>Your workspace is behind a one-time email link. No password to remember.</p>
              <a className="button button--coral button--full" href="/fitlife-x/login">Email me a sign-in link</a>
            </div>
          </section>
        </div>
      </SiteLayout>
    );
  }

  const member = session.member;
  const entitled = session.entitled;

  return (
    <SiteLayout>
      <div className="fx-page">
        <section className="fx-portal-top">
          <div className="site-container">
            <div>
              <p className="eyebrow eyebrow--light">FitLife-X Pro workspace</p>
              <h1>{member.businessName || member.name || member.email}</h1>
              <p>{member.email}{member.plan ? ` · ${member.plan} plan` : ""}</p>
            </div>
            <div className="fx-portal-top__actions">
              <button className="button button--outline-light" onClick={() => billingPortal.mutate()} disabled={billingPortal.isPending}>
                <CreditCard size={15} />{billingPortal.isPending ? "Opening…" : "Billing"}
              </button>
              <button className="button button--outline-light" onClick={() => logout.mutate()}>
                <LogOut size={15} />Sign out
              </button>
            </div>
          </div>
        </section>

        {/* A lapsed member keeps the page and the billing button; only the
            deliverables are withheld. */}
        {!entitled && (
          <section className="fx-lapsed">
            <div className="site-container">
              <AlertTriangle size={19} />
              <div>
                <strong>Your subscription is not active.</strong>
                <span>
                  {member.subscriptionStatus === "none"
                    ? "Choose a plan to open the Pro workspace."
                    : "Update your payment method in the billing portal and everything here comes straight back."}
                </span>
              </div>
              <a className="button button--coral" href={member.subscriptionStatus === "none" ? "/fitlife-x#subscribe" : "#"}
                 onClick={event => { if (member.subscriptionStatus !== "none") { event.preventDefault(); billingPortal.mutate(); } }}>
                {member.subscriptionStatus === "none" ? "See plans" : "Fix billing"}
              </a>
            </div>
          </section>
        )}

        <section className="section">
          <div className="site-container">
            <nav className="fx-tabs" aria-label="Workspace sections">
              {([
                ["forms", "Forms library", FileText],
                ["sop", "SOP development", ClipboardList],
                ["advertising", "Advertising", Megaphone],
                ["requests", "My requests", null],
              ] as const).map(([key, label, Icon]) => (
                <button
                  key={key}
                  type="button"
                  className={tab === key ? "is-current" : undefined}
                  aria-current={tab === key ? "page" : undefined}
                  onClick={() => setTab(key)}
                >
                  {Icon && <Icon size={14} />}{label}
                </button>
              ))}
            </nav>

            {tab === "forms" && <FormsPanel entitled={entitled} />}
            {tab === "sop" && <SopPanel entitled={entitled} />}
            {tab === "advertising" && <AdvertisingPanel />}
            {tab === "requests" && <RequestsPanel />}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}

function Locked({ what }: { what: string }) {
  return (
    <div className="fx-locked">
      <AlertTriangle size={18} />
      <p>{what} is included with an active Pro membership. Restart your subscription and it unlocks immediately.</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Forms library                                                              */
/* -------------------------------------------------------------------------- */

function FormsPanel({ entitled }: { entitled: boolean }) {
  const utils = trpc.useUtils();
  const [selected, setSelected] = useState<string[]>([]);
  const submit = trpc.fitlifeX.submitFormsRequest.useMutation({
    onSuccess: () => {
      toast.success("Request received. We will come back with your branded set.");
      setSelected([]);
      void utils.fitlifeX.myRequests.invalidate();
    },
    onError: error => toast.error("That did not send", { description: error.message }),
  });

  if (!entitled) return <Locked what="The white-label forms library" />;

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (selected.length === 0) {
      toast.error("Pick at least one form.");
      return;
    }
    const data = new FormData(event.currentTarget);
    const brandColor = String(data.get("brandColor") ?? "").trim();
    submit.mutate({
      requestedForms: selected,
      brandName: String(data.get("brandName") ?? "").trim(),
      // Sent only when filled. An empty string would fail the hex validation
      // server-side and read as a form error rather than an omission.
      ...(brandColor ? { brandColor } : {}),
      ...(String(data.get("notes") ?? "").trim() ? { notes: String(data.get("notes")).trim() } : {}),
    });
  }

  return (
    <div className="fx-panel">
      <header>
        <h2>White-label forms</h2>
        <p>Pick what you need. We prepare each one under your brand and send the set back ready to use.</p>
      </header>
      <form onSubmit={onSubmit}>
        <fieldset className="fx-checks">
          <legend>Forms required *</legend>
          {FORM_LIBRARY.map(form => (
            <label key={form}>
              <input
                type="checkbox"
                checked={selected.includes(form)}
                onChange={event =>
                  setSelected(current => (event.target.checked ? [...current, form] : current.filter(item => item !== form)))
                }
              />
              <span>{form}</span>
            </label>
          ))}
        </fieldset>
        <div className="fx-grid-2">
          <label className="field">
            <span>Brand name as it should appear *</span>
            <input name="brandName" required maxLength={160} />
          </label>
          <label className="field">
            <span>Brand colour</span>
            <input name="brandColor" placeholder="#0F2044" pattern="^#[0-9a-fA-F]{6}$" title="Six-digit hex, e.g. #0F2044" />
          </label>
        </div>
        <label className="field">
          <span>Anything specific we should know</span>
          <textarea name="notes" rows={4} maxLength={4000} />
        </label>
        <button className="button button--coral button--full" disabled={submit.isPending}>
          {submit.isPending ? "Sending…" : `Request ${selected.length || ""} form${selected.length === 1 ? "" : "s"}`.trim()}
        </button>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* SOP development                                                            */
/* -------------------------------------------------------------------------- */

function SopPanel({ entitled }: { entitled: boolean }) {
  const utils = trpc.useUtils();
  const models = trpc.fitlifeX.sopModels.useQuery();
  const [model, setModel] = useState<"independent" | "multi-site" | "amenity" | null>(null);
  const submit = trpc.fitlifeX.submitSopRequest.useMutation({
    onSuccess: () => {
      toast.success("Questionnaire received. We will send your SOP for review.");
      setModel(null);
      void utils.fitlifeX.myRequests.invalidate();
    },
    onError: error => toast.error("That did not send", { description: error.message }),
  });

  if (!entitled) return <Locked what="SOP development" />;
  if (models.isLoading || !models.data) return <div className="fx-panel">Loading the questionnaire…</div>;

  const { fields, shared, models: list } = models.data;
  const chosen = list.find(entry => entry.key === model);
  // Shared questions first, then the ones this model adds - so changing the
  // model never reshuffles what the member has already filled in.
  const tokens = chosen ? [...shared, ...chosen.extraTokens] : [];

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!chosen) return;
    const data = new FormData(event.currentTarget);
    const answers: Record<string, string> = {};
    for (const token of tokens) {
      const value = String(data.get(token) ?? "").trim();
      if (value) answers[token] = value;
    }
    submit.mutate({ model: chosen.key, answers });
  }

  return (
    <div className="fx-panel">
      <header>
        <h2>SOP development</h2>
        <p>
          Start from the model closest to your facility. Each one is a complete operating standard —
          you will receive the base document to review, and the answers below are what we write it around.
        </p>
      </header>

      <div className="fx-models">
        {list.map(entry => (
          <button
            key={entry.key}
            type="button"
            className={model === entry.key ? "is-selected" : undefined}
            onClick={() => setModel(entry.key)}
          >
            <strong>{entry.title}</strong>
            <span>{entry.summary}</span>
            <small>Template v{entry.version}</small>
          </button>
        ))}
      </div>

      {chosen && (
        <form onSubmit={onSubmit} className="fx-sop-form">
          <p className="fx-sop-form__note">
            Leave anything you are unsure of blank — we will flag it rather than guess. Nothing is
            delivered with a gap left in it.
          </p>
          {tokens.map(token => {
            const field = fields[token];
            if (!field) return null;
            return (
              <label className="field" key={token}>
                <span>{field.label}</span>
                {field.long ? (
                  <textarea name={token} rows={3} maxLength={2000} />
                ) : (
                  <input name={token} maxLength={2000} />
                )}
                {field.hint && <small className="fx-hint">{field.hint}</small>}
              </label>
            );
          })}
          <button className="button button--coral button--full" disabled={submit.isPending}>
            {submit.isPending ? "Sending…" : "Submit questionnaire"}
          </button>
        </form>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Advertising                                                                */
/* -------------------------------------------------------------------------- */

function AdvertisingPanel() {
  const utils = trpc.useUtils();
  const [placements, setPlacements] = useState<string[]>([]);
  const submit = trpc.fitlifeX.submitAdRequest.useMutation({
    onSuccess: () => {
      toast.success("Enquiry received. We will come back with placement options and a quote.");
      setPlacements([]);
      void utils.fitlifeX.myRequests.invalidate();
    },
    onError: error => toast.error("That did not send", { description: error.message }),
  });

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (placements.length === 0) {
      toast.error("Pick at least one placement.");
      return;
    }
    const data = new FormData(event.currentTarget);
    const optional = (key: string) => {
      const value = String(data.get(key) ?? "").trim();
      return value ? { [key]: value } : {};
    };
    submit.mutate({
      company: String(data.get("company") ?? "").trim(),
      productSummary: String(data.get("productSummary") ?? "").trim(),
      placements,
      ...optional("website"),
      ...optional("audienceFit"),
      ...optional("budgetBand"),
      ...optional("startWindow"),
      ...optional("notes"),
    });
  }

  return (
    <div className="fx-panel">
      <header>
        <h2>Advertise with Altitude</h2>
        <p>
          Placement is quoted rather than bought off a shelf — inventory and audience fit are
          judgement calls, and we would rather turn down a poor match than take the money.
        </p>
      </header>
      <form onSubmit={onSubmit}>
        <div className="fx-grid-2">
          <label className="field">
            <span>Company *</span>
            <input name="company" required maxLength={200} />
          </label>
          <label className="field">
            <span>Website</span>
            <input name="website" type="url" placeholder="https://" maxLength={400} />
          </label>
        </div>
        <label className="field">
          <span>What do you sell? *</span>
          <textarea name="productSummary" required rows={4} maxLength={4000} />
        </label>
        <label className="field">
          <span>Why is it a fit for gym operators?</span>
          <textarea name="audienceFit" rows={3} maxLength={4000} />
        </label>
        <fieldset className="fx-checks">
          <legend>Placements of interest *</legend>
          {PLACEMENTS.map(placement => (
            <label key={placement}>
              <input
                type="checkbox"
                checked={placements.includes(placement)}
                onChange={event =>
                  setPlacements(current => (event.target.checked ? [...current, placement] : current.filter(item => item !== placement)))
                }
              />
              <span>{placement}</span>
            </label>
          ))}
        </fieldset>
        <div className="fx-grid-2">
          <label className="field">
            <span>Budget range</span>
            <input name="budgetBand" maxLength={120} placeholder="e.g. $2–5k per quarter" />
          </label>
          <label className="field">
            <span>When would you want to start?</span>
            <input name="startWindow" maxLength={120} placeholder="e.g. Q1 2027" />
          </label>
        </div>
        <label className="field">
          <span>Anything else</span>
          <textarea name="notes" rows={3} maxLength={4000} />
        </label>
        <button className="button button--coral button--full" disabled={submit.isPending}>
          {submit.isPending ? "Sending…" : "Send enquiry"}
        </button>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* My requests                                                                */
/* -------------------------------------------------------------------------- */

const STATUS_LABEL: Record<string, string> = {
  submitted: "Received",
  in_progress: "In progress",
  in_review: "In review",
  awaiting_client: "Waiting on you",
  delivered: "Delivered",
  cancelled: "Cancelled",
  quoted: "Quoted",
  booked: "Booked",
  live: "Live",
  completed: "Completed",
  declined: "Declined",
};

function RequestsPanel() {
  const requests = trpc.fitlifeX.myRequests.useQuery();
  if (requests.isLoading || !requests.data) return <div className="fx-panel">Loading…</div>;
  const { forms, sops, ads } = requests.data;
  const empty = forms.length + sops.length + ads.length === 0;

  return (
    <div className="fx-panel">
      <header>
        <h2>My requests</h2>
        <p>Everything you have sent us, and where it has got to.</p>
      </header>
      {empty ? (
        <p className="fx-empty">Nothing submitted yet. Start with the forms library or an SOP.</p>
      ) : (
        <ul className="fx-requests">
          {forms.map(row => (
            <li key={`f${row.id}`}>
              <span className="fx-requests__kind"><FileText size={13} />Forms</span>
              <strong>{row.brandName}</strong>
              <span>{row.requestedForms.length} form{row.requestedForms.length === 1 ? "" : "s"}</span>
              <em data-status={row.status}>{STATUS_LABEL[row.status] ?? row.status}</em>
            </li>
          ))}
          {sops.map(row => (
            <li key={`s${row.id}`}>
              <span className="fx-requests__kind"><ClipboardList size={13} />SOP</span>
              <strong>{row.model}</strong>
              <span>Questionnaire submitted</span>
              <em data-status={row.status}>{STATUS_LABEL[row.status] ?? row.status}</em>
            </li>
          ))}
          {ads.map(row => (
            <li key={`a${row.id}`}>
              <span className="fx-requests__kind"><Megaphone size={13} />Advertising</span>
              <strong>{row.company}</strong>
              <span>{row.placements.join(", ")}</span>
              <em data-status={row.status}>{STATUS_LABEL[row.status] ?? row.status}</em>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
